// client/src/components/pages/LexiStar/Replay/ReplayMultiTimeline.tsx
//
// ST-00277 — the timeline of a replay with several trucks: ONE scrubber over one lane per truck.
// Each lane is named by the truck (its own colour and registration) and shows, over the
// window, the status colour the organisation's display rules gave the truck at each moment (the
// stage colours by default, ./replayDisplay displayBands) and a thin mark per event. Before the
// first fix and after the last the lane stays empty; a truck that sent nothing says so. The
// lanes of trucks whose track is switched off are dimmed. One playhead crosses every lane.
//
// The lanes are the scrubber (role slider), as in the single-truck timeline (./ReplayTimeline):
// press or drag anywhere on them to seek, with the clock holding still under the pointer
// (onScrubStart / onScrubEnd, ST-00245); touch drags scrub (touch-action: pan-y keeps the page
// scrolling vertically); ← → step a minute (Shift: ten), PageUp / PageDown an hour, Home / End.
// The lanes are memoised apart from the playhead: a frame of the clock (15 a second) moves the
// playhead only, not 25 trucks' bands and marks (measured on a 25-truck replay).
//
// ST-00311 (up to 100 trucks): past about 25 lanes the lanes scroll inside the card (LANES_MAX_H)
// under a time axis that stays in view, so the transport above them does not scroll away. The
// scroll box wraps the scrubber rather than being it, so a click on its scroll bar never seeks;
// it reaches into the card's padding (-mx-4 px-4) so the last time label and the playhead's dot,
// centred on the window's end, are not cut off.

import { type KeyboardEvent, type PointerEvent, memo, useCallback, useRef } from 'react';
import { assetStatusVar, type AssetStatusKey } from '../../../map/assetStatus';
import { clockDateTime, clockTime, tickLabel, timeTicks } from './replayLogic';
import type { DisplayBand } from './replayDisplay';

const MINUTE = 60000;
/** The lane labels' width (the playhead area starts after it). */
const LABEL = '6.75rem';
/** The lanes' box: 25 lanes and the axis fit (18 px a lane); more scroll inside it (ST-00311). */
const LANES_MAX_H = 'max-h-[30rem]';

const pct = (t: number, start: number, end: number) =>
  `${Math.max(0, Math.min(100, ((t - start) / (end - start)) * 100))}%`;
const width = (a: number, b: number, start: number, end: number) =>
  `${Math.max(0.2, Math.min(100, ((Math.min(b, end) - Math.max(a, start)) / (end - start)) * 100))}%`;

export interface TruckLane {
  key: string;
  label: string;
  /** The truck's own colour (its track and tag). */
  color: string;
  /** The display over time (status colours). */
  bands: readonly DisplayBand[];
  /** Event times. */
  events: readonly number[];
  /** Its track is switched off. */
  dim?: boolean;
  /** No positions in the window. */
  empty?: boolean;
}

export interface ReplayMultiTimelineProps {
  start: number;
  end: number;
  cursor: number;
  onSeek: (t: number) => void;
  onScrubStart?: () => void;
  onScrubEnd?: () => void;
  lanes: readonly TruckLane[];
}

/** The time axis: whole hours (or finer on short windows), SAST. */
const Ticks = memo(function Ticks({ start, end }: { start: number; end: number }) {
  const ticks = timeTicks(start, end);
  return (
    <div className="flex items-center mb-1">
      <span className="flex-shrink-0" style={{ width: LABEL }} />
      <div className="relative flex-1 h-4 text-[10px] text-slate-500 tabular-nums">
        {ticks.map((t, i) => (
          <span
            key={t}
            className={`absolute -translate-x-1/2 whitespace-nowrap ${ticks.length > 5 && i % 2 === 1 ? 'hidden sm:block' : ''}`}
            style={{ left: pct(t, start, end) }}
          >
            {tickLabel(t)}
          </span>
        ))}
      </div>
    </div>
  );
});

/** One row per truck: its name, its status colours over time, its event marks. */
const LaneRows = memo(function LaneRows({
  lanes,
  start,
  end,
}: {
  lanes: readonly TruckLane[];
  start: number;
  end: number;
}) {
  return (
    <>
      {lanes.map((lane) => (
        <div key={lane.key} className={`flex items-center ${lane.dim ? 'opacity-40' : ''}`} data-lane={lane.key}>
          <span
            className="flex-shrink-0 flex items-center gap-1.5 pr-2 min-w-0 text-[11px] font-medium text-slate-700"
            style={{ width: LABEL }}
          >
            <span className="inline-block w-2.5 h-2.5 flex-shrink-0 rounded-sm" style={{ background: lane.color }} />
            <span className="truncate" title={lane.label}>
              {lane.label}
            </span>
          </span>
          <div className="relative flex-1 h-3.5">
            <div className="absolute inset-0 rounded bg-slate-100 ring-1 ring-inset ring-slate-300" />
            {lane.empty && (
              <span className="absolute inset-y-0 left-1.5 flex items-center text-[10px] italic text-slate-500">
                No positions in this window
              </span>
            )}
            {lane.bands.map((b, i) => (
              <span
                key={`${b.from}-${i}`}
                className="absolute top-0 bottom-0 rounded-sm"
                data-display-band={b.display.base.token}
                style={{
                  left: pct(b.from, start, end),
                  width: width(b.from, Math.max(b.to, b.from + 1), start, end),
                  background: assetStatusVar(b.display.base.token as AssetStatusKey, 'solid'),
                }}
                title={`${lane.label} · ${b.display.base.label}: ${clockTime(b.from, false)}–${clockTime(b.to, false)}`}
              />
            ))}
            {lane.events.map((t, i) => (
              <span
                key={`e-${t}-${i}`}
                aria-hidden="true"
                className="absolute -top-0.5 -bottom-0.5 w-0.5 -translate-x-1/2 rounded-full bg-slate-900"
                style={{ left: pct(t, start, end) }}
              />
            ))}
          </div>
        </div>
      ))}
    </>
  );
});

export default function ReplayMultiTimeline({
  start,
  end,
  cursor,
  onSeek,
  onScrubStart,
  onScrubEnd,
  lanes,
}: ReplayMultiTimelineProps) {
  const areaRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef<{ pointerId: number | undefined } | null>(null);

  const tAtX = useCallback(
    (clientX: number): number | null => {
      const el = areaRef.current;
      if (!el || !Number.isFinite(clientX)) return null;
      const r = el.getBoundingClientRect();
      if (!r.width) return null;
      const f = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
      return start + f * (end - start);
    },
    [start, end]
  );
  const seekToX = (clientX: number) => {
    const t = tAtX(clientX);
    if (t !== null) onSeek(t);
  };
  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== undefined && e.button !== 0) return;
    drag.current = { pointerId: e.pointerId };
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch {
      /* no capture (synthetic pointer) */
    }
    onScrubStart?.();
    seekToX(e.clientX);
  };
  const isOurs = (e: PointerEvent<HTMLDivElement>) =>
    drag.current !== null &&
    (drag.current.pointerId === undefined || e.pointerId === undefined || e.pointerId === drag.current.pointerId);
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (isOurs(e)) seekToX(e.clientX);
  };
  const endDrag = (e: PointerEvent<HTMLDivElement>, apply: boolean) => {
    if (!isOurs(e)) return;
    if (apply) seekToX(e.clientX);
    drag.current = null;
    onScrubEnd?.();
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 10 * MINUTE : MINUTE;
    let next: number | null = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = cursor + step;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = cursor - step;
    else if (e.key === 'PageUp') next = cursor + 60 * MINUTE;
    else if (e.key === 'PageDown') next = cursor - 60 * MINUTE;
    else if (e.key === 'Home') next = start;
    else if (e.key === 'End') next = end;
    if (next === null) return;
    e.preventDefault();
    onSeek(Math.max(start, Math.min(end, next)));
  };

  return (
    <div
      className={`select-none ${LANES_MAX_H} overflow-y-auto overflow-x-hidden -mx-4 px-4 pb-1`}
      data-testid="replay-multi-lanes-box"
    >
      <div className="sticky top-0 z-10 bg-surface">
        <Ticks start={start} end={end} />
      </div>
      <div
        role="slider"
        tabIndex={0}
        aria-label="Replay position"
        aria-valuemin={start}
        aria-valuemax={end}
        aria-valuenow={Math.round(cursor)}
        aria-valuetext={clockDateTime(cursor)}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={(e) => endDrag(e, true)}
        onPointerCancel={(e) => endDrag(e, false)}
        onLostPointerCapture={(e) => endDrag(e, false)}
        onKeyDown={onKeyDown}
        className="relative space-y-1 py-1 rounded-md cursor-pointer active:cursor-grabbing touch-pan-y focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        data-testid="replay-multi-lanes"
      >
        <LaneRows lanes={lanes} start={start} end={end} />
        <div
          ref={areaRef}
          className="pointer-events-none absolute top-0 bottom-0 right-0"
          style={{ left: LABEL }}
          aria-hidden="true"
        >
          <div
            className="absolute top-0 bottom-0 flex flex-col items-center -translate-x-1/2"
            style={{ left: pct(cursor, start, end) }}
            data-testid="replay-playhead"
          >
            <span className="-mt-1 w-3 h-3 flex-shrink-0 rounded-full bg-slate-900 ring-2 ring-surface shadow" />
            <span className="flex-1 w-0.5 bg-slate-900" />
          </div>
        </div>
      </div>
    </div>
  );
}
